/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useTheme } from "@cd2/theme/build/es6"
import { IRecord } from "../types/schema"
import { Grid, Icon, Spinner, IconButton, useMediaQuery } from "@cd2/components/build/es6"
import React, { useState } from "react"
import { useFileboy } from "@cd2/fileboy-react"
import { useSkeem } from "../providers/SkeemProvider"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { pluralize } from "@cd2/tools"
import { P } from "@cd2/text/build/es6"
import { renderNullOnError } from "../utils/renderNullOnError"
import { useInterval } from "../hooks/useInterval"
import { useReloadKey } from "../hooks/useReloadKey"

export const ResultPdfGenerationStatus = renderNullOnError(ResultPdfGenerationStatusComponent)

function ResultPdfGenerationStatusComponent() {
  const theme = useTheme()
  const skeem = useSkeem()
  const fileboy = useFileboy()

  const isMobile = useMediaQuery({ maxWidth: 767 })

  const [collapsed, setCollapsed] = useState(false)
  const [resultPdfs, setResultPdfs] = useState<
    IRecord<"resultPdfs", "name" | "status" | "fileboyId">[]
  >([])

  const [reloadKey, triggerReload] = useReloadKey()

  useAsyncEffect(async () => {
    const newRecords = await skeem.fetch<(typeof resultPdfs)[0]>({
      resultPdfs: {
        attributes: ["name", "fileboyId", "status"],
        filter: {
          matches: {
            user: { session: true },
            hidden: false,
          },
        },
        sort: { by: "createdAt", direction: "asc" },
      },
    })

    setResultPdfs(newRecords)
  }, [reloadKey])

  useInterval(triggerReload, 5000)

  const icons: Record<IRecord<"resultPdfs", "status">["status"], React.ReactNode> = {
    "": <Spinner size={40} color={theme.themeColor("primary")} />,
    pending: <Spinner size={40} color={theme.themeColor("primary")} />,
    processing: <Spinner size={40} color={theme.themeColor("primary")} />,
    done: <Icon icon="file-pdf" size={20} color={"#d93026"} />,
    error: <Icon icon="exclamation-triangle" size={18} color={theme.themeColor("orange")} />,
  }

  async function handleDownload(record: IRecord<"resultPdfs", "name" | "status" | "fileboyId">) {
    if (!record.fileboyId) {
      return
    }
    const aTag = document.createElement("a")
    aTag.setAttribute("target", "_blank")
    aTag.setAttribute("download", "true")
    aTag.href = fileboy.buildFileUrl(record.fileboyId, {
      download: true,
      filename: record.name,
    })
    document.body.appendChild(aTag)
    aTag.click()
    aTag.remove()

    await skeem.mutate({
      resultPdfs: { update: { id: record.id, status: record.status, hidden: true } },
    })
  }

  async function handleHide(record: IRecord<"resultPdfs", "name" | "status" | "fileboyId">) {
    setResultPdfs(resultPdfs => resultPdfs.filter(resultPdf => resultPdf.id !== record.id))
    await skeem.mutate({
      resultPdfs: { update: { id: record.id, status: record.status, hidden: true } },
    })
  }

  if (resultPdfs.length === 0) {
    return <React.Fragment />
  }

  let title = "Case Coaching PDFs"

  if (resultPdfs.every(pdf => pdf.status === "done")) {
    title = `${pluralize(resultPdfs.length, "PDF", "PDFs")} ready`
  } else if (resultPdfs.some(pdf => pdf.status === "error")) {
    title = `${pluralize(
      resultPdfs.filter(pdf => pdf.status === "error").length,
      "PDF",
      "PDFs",
    )} failed`
  } else if (resultPdfs.some(pdf => pdf.status === "pending")) {
    title = `${pluralize(
      resultPdfs.filter(pdf => pdf.status === "pending").length,
      "PDF",
      "PDFs",
    )} waiting to be processed`
  } else if (resultPdfs.some(pdf => pdf.status === "processing")) {
    title = `${pluralize(
      resultPdfs.filter(pdf => pdf.status === "processing").length,
      "PDF",
      "PDFs",
    )} generating`
  }

  return (
    <div
      css={css`
        position: fixed;
        bottom: 16px;
        right: 16px;
        box-shadow: ${theme.boxShadow.md}, ${theme.boxShadow.xxl};
        z-index: 10000;
        border-radius: 8px 8px 4px 4px;
        overflow: hidden;
        width: ${isMobile ? 288 : 350}px;
      `}
    >
      <Grid
        css={css`
          color: white;
          background: ${theme.themeColor("primary")};
          padding: 8px 16px;
          font-weight: bold;
        `}
        columns={["auto", 40]}
        alignItems="center"
      >
        <P color="secondary" blockSpacing={0}>
          {title}
        </P>
        <IconButton
          icon={collapsed ? "chevron-up" : "chevron-down"}
          onClick={() => setCollapsed(!collapsed)}
          buttonStyle="text"
          color="secondary"
        />
      </Grid>
      <div
        css={css`
          max-height: 400px;
          overflow-y: scroll;
        `}
      >
        {!collapsed &&
          resultPdfs.map(pdf => (
            <Grid
              key={pdf.id}
              columns={[40, "auto", "max-content", "max-content"]}
              css={css`
                padding: 8px 16px;
                background: white;
              `}
              alignItems="center"
              gap={8}
            >
              <div
                css={css`
                  justify-self: center;
                `}
              >
                {icons[pdf.status]}
              </div>
              <div>{pdf.name}</div>
              {pdf.status === "done" ? (
                <IconButton
                  icon="download"
                  buttonStyle="text"
                  onClick={() => handleDownload(pdf)}
                />
              ) : (
                <div />
              )}
              <IconButton icon="times" buttonStyle="text" onClick={() => handleHide(pdf)} />
            </Grid>
          ))}
      </div>
    </div>
  )
}
